import React, { useRef, useState, useMemo } from "react";
import { Controller } from "react-hook-form";
import {
    Box,
    FormControl,
    FormHelperText,
    Typography,
    Button,
    Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { buildValidationRules, HELPER_TEXT_STYLES } from "../fieldHelpers";

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const parseAcceptToExtensions = (accept) => {
    // accept: ".docx,.odt" or "application/pdf" etc.
    if (!accept) return [];
    return accept
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
};

const isFileAccepted = (file, acceptList) => {
    if (!file) return true;
    if (!acceptList || acceptList.length === 0) return true;

    const fileName = (file.name || "").toLowerCase();

    // extension-based accept: ".docx"
    const extAccepts = acceptList.filter((x) => x.startsWith(".")).map((x) => x.toLowerCase());
    if (extAccepts.length > 0) {
        return extAccepts.some((ext) => fileName.endsWith(ext));
    }

    // mime-based accept: "application/pdf"
    const mimeAccepts = acceptList.filter((x) => !x.startsWith(".")).map((x) => x.toLowerCase());
    if (mimeAccepts.length > 0) {
        const mime = (file.type || "").toLowerCase();
        return mimeAccepts.some((m) => mime === m || (m.endsWith("/*") && mime.startsWith(m.replace("/*", "/"))));
    }

    return true;
};

const FileUploadFieldRenderer = ({ field, control }) => {
    const {
        name,
        label,
        required = false,
        fullWidth = true,
        disabled = false,
        readOnly = false,
        accept = "",
        helperText = "",
        maxSizeBytes,
        buttonText = "Browse",
        dropText = "Drag & drop here, or click to browse",
        onChange: externalOnChange,
        error: externalError,
        helperText: externalHelperText,
    } = field;

    const rules = buildValidationRules(field);

    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const acceptList = useMemo(() => parseAcceptToExtensions(accept), [accept]);

    const validateFile = (file) => {
        if (!file) return true;

        if (!isFileAccepted(file, acceptList)) {
            return `Invalid file type. Allowed: ${acceptList.join(", ") || accept}`;
        }

        if (maxSizeBytes && file.size > maxSizeBytes) {
            return `File is too large. Max: ${formatBytes(maxSizeBytes)}`;
        }

        return true;
    };

    const pickFile = () => {
        if (disabled || readOnly) return;
        if (inputRef.current) inputRef.current.click();
    };

    return (
        <Controller
            key={name}
            name={name}
            control={control}
            rules={{
                ...rules,
                validate: (value) => {
                    // value is a File object
                    if (required && !value) return `${label} is required`;
                    const res = validateFile(value);
                    return res === true ? true : res;
                },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
                const hasExternalError = Boolean(externalError);
                const hasRHFError = Boolean(error);
                const showError = hasExternalError || hasRHFError;

                const finalHelperText = (externalHelperText || error?.message || helperText || "").toString();

                const file = value || null;

                return (
                    <FormControl fullWidth={fullWidth} required={required} disabled={disabled} error={showError}>
                        {!!label && (
                            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                                {label}
                            </Typography>
                        )}

                        <Box
                            onClick={pickFile}
                            onDragEnter={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (disabled || readOnly) return;
                                setIsDragging(true);
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (disabled || readOnly) return;
                                setIsDragging(true);
                            }}
                            onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsDragging(false);
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsDragging(false);
                                if (disabled || readOnly) return;

                                const dropped = e.dataTransfer?.files?.[0];
                                if (!dropped) return;

                                const validation = validateFile(dropped);
                                if (validation !== true) {
                                    // RHF will show error via validate() on submit,
                                    // but we update immediately for UX
                                    onChange(dropped);
                                    if (typeof externalOnChange === "function") externalOnChange(dropped);
                                    return;
                                }

                                onChange(dropped);
                                if (typeof externalOnChange === "function") externalOnChange(dropped);
                            }}
                            sx={{
                                borderRadius: 2,
                                border: "1px dashed",
                                borderColor: showError ? "error.main" : isDragging ? "primary.main" : "divider",
                                bgcolor: isDragging ? "action.hover" : "background.paper",
                                px: 2,
                                py: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                cursor: disabled || readOnly ? "not-allowed" : "pointer",
                                transition: "all 120ms ease-in-out",
                                boxShadow: isDragging ? 2 : 0,
                                "&:hover": {
                                    bgcolor: disabled || readOnly ? "background.paper" : "action.hover",
                                    borderColor: disabled || readOnly ? "divider" : "primary.main",
                                },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                                <CloudUploadIcon fontSize="small" />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                        {file ? file.name : dropText}
                                    </Typography>

                                    {file ? (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25, flexWrap: "wrap" }}>
                                            <Chip size="small" variant="outlined" label={formatBytes(file.size)} />
                                            {accept ? <Chip size="small" variant="outlined" label={`Allowed: ${accept}`} /> : null}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            {accept ? `Allowed: ${accept}` : ""}
                                            {maxSizeBytes ? ` • Max: ${formatBytes(maxSizeBytes)}` : ""}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Button
                                type="button"
                                variant="contained"
                                size="small"
                                disabled={disabled || readOnly}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    pickFile();
                                }}
                            >
                                {buttonText}
                            </Button>

                            <input
                                ref={inputRef}
                                type="file"
                                accept={accept}
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    const selected = e.target.files?.[0] || null;
                                    onChange(selected);
                                    if (typeof externalOnChange === "function") externalOnChange(selected);
                                }}
                            />
                        </Box>

                        {finalHelperText ? (
                            <FormHelperText sx={HELPER_TEXT_STYLES}>{finalHelperText}</FormHelperText>
                        ) : null}
                    </FormControl>
                );
            }}
        />
    );
};

export default FileUploadFieldRenderer;
